import { cn } from '@Utils/cn';
import { useCallback, useEffect, useState } from 'react';
import {
  ArrowLeft,
  ChevronRight,
  Database,
  Users,
  Calendar,
  CalendarDays,
  Activity,
  Loader2,
  PanelRightClose,
} from 'lucide-react';
import service from '@Service';
import type { BusinessObject, BusinessObjectMap } from '@Types/soul';

const ENTITY_ICONS: Record<string, React.ElementType> = {
  fish: Users,
  reservation: Calendar,
  event: CalendarDays,
  activity: Activity,
};

// Columns to display per entity type
const ENTITY_COLUMNS: Record<string, { key: string; label: string }[]> = {
  fish: [
    { key: 'fullName', label: 'Name' },
    { key: 'email', label: 'Email' },
    { key: 'phoneNumber', label: 'Phone' },
    { key: 'subscriptionTier', label: 'Tier' },
    { key: 'status', label: 'Status' },
  ],
  reservation: [
    { key: 'fishName', label: 'Guest' },
    { key: 'venue', label: 'Venue' },
    { key: 'date', label: 'Date' },
    { key: 'status', label: 'Status' },
    { key: 'partySize', label: 'Party' },
  ],
  event: [
    { key: 'name', label: 'Name' },
    { key: 'venue', label: 'Venue' },
    { key: 'date', label: 'Date' },
    { key: 'status', label: 'Status' },
    { key: 'attendeeCount', label: 'Attendees' },
  ],
  activity: [
    { key: 'type', label: 'Type' },
    { key: 'location', label: 'Location' },
    { key: 'activityDate', label: 'Date' },
    { key: 'status', label: 'Status' },
    { key: 'amount', label: 'Amount' },
  ],
};

function timeAgo(iso: string): string {
  const seconds = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

function formatCellValue(value: any, key: string): string {
  if (value == null) return '—';
  if (key === 'amount' && typeof value === 'number') return `$${value.toFixed(2)}`;
  if ((key === 'date' || key === 'activityDate') && typeof value === 'string') {
    try {
      return new Date(value).toLocaleDateString();
    } catch {
      return value;
    }
  }
  if (Array.isArray(value)) return value.join(', ');
  return String(value);
}

const PAGE_SIZE = 50;

interface BusinessObjectPanelProps {
  businessObjects: BusinessObjectMap;
  chatId: string | null;
  isOpen: boolean;
  onToggle: () => void;
}

export default function BusinessObjectPanel({
  businessObjects,
  chatId,
  isOpen,
  onToggle,
}: BusinessObjectPanelProps) {
  const entries = Object.values(businessObjects);

  // Detail view state
  const [selectedBo, setSelectedBo] = useState<BusinessObject | null>(null);
  const [records, setRecords] = useState<Record<string, any>[]>([]);
  const [recordsTotal, setRecordsTotal] = useState(0);
  const [recordsOffset, setRecordsOffset] = useState(0);
  const [loading, setLoading] = useState(false);

  // Reset detail view when chat changes
  useEffect(() => {
    setSelectedBo(null);
    setRecords([]);
  }, [chatId]);

  const loadRecords = useCallback(
    async (bo: BusinessObject, offset = 0) => {
      if (!chatId) return;
      setLoading(true);
      try {
        const result = await service.getBusinessObjectRecords(chatId, bo.key, {
          limit: PAGE_SIZE,
          offset,
        });
        setRecords(result.records);
        setRecordsTotal(result.total);
        setRecordsOffset(offset);
      } catch (err) {
        console.error('Failed to load BO records:', err);
        setRecords([]);
      } finally {
        setLoading(false);
      }
    },
    [chatId],
  );

  // When the selected BO gets updated during streaming, refresh records
  useEffect(() => {
    if (!selectedBo) return;
    const updated = businessObjects[selectedBo.key];
    if (!updated) {
      // BO was cleared
      setSelectedBo(null);
      setRecords([]);
      return;
    }
    if (updated.updatedAt !== selectedBo.updatedAt) {
      setSelectedBo(updated);
      loadRecords(updated, 0);
    }
  }, [businessObjects, selectedBo, loadRecords]);

  const openDetail = useCallback(
    (bo: BusinessObject) => {
      setSelectedBo(bo);
      loadRecords(bo, 0);
    },
    [loadRecords],
  );

  const goBack = useCallback(() => {
    setSelectedBo(null);
    setRecords([]);
  }, []);

  const columns = selectedBo ? ENTITY_COLUMNS[selectedBo.entityType] ?? [] : [];
  const hasMore = selectedBo && recordsOffset + PAGE_SIZE < recordsTotal;
  const hasPrev = recordsOffset > 0;

  return (
    <>
      {/* Toggle button — always visible */}
      <button
        onClick={onToggle}
        className={cn(
          'absolute right-0 top-0 z-10 flex items-center gap-1 border-b border-l border-zinc-200 bg-zinc-50 px-2 py-1.5 text-xs text-zinc-500 transition-colors hover:bg-zinc-100',
          isOpen && 'bg-zinc-100',
        )}
        title={isOpen ? 'Hide data sets' : 'Show data sets'}
      >
        {isOpen ? (
          <PanelRightClose className="size-3.5" />
        ) : (
          <Database className="size-3.5" />
        )}
        {!isOpen && entries.length > 0 && (
          <span className="ml-0.5 rounded-full bg-zinc-900 px-1.5 py-0.5 text-[10px] font-medium text-white">
            {entries.length}
          </span>
        )}
      </button>

      {/* Panel */}
      <div
        className={cn(
          'shrink-0 border-l border-zinc-200 bg-zinc-50 transition-[width] duration-200 overflow-hidden',
          isOpen ? (selectedBo ? 'w-[480px]' : 'w-72') : 'w-0',
        )}
      >
        <div
          className={cn('flex h-full flex-col', selectedBo ? 'w-[480px]' : 'w-72')}
        >
          {/* Header */}
          <div className="flex items-center gap-2 border-b border-zinc-200 px-3 py-2">
            {selectedBo ? (
              <>
                <button
                  onClick={goBack}
                  className="rounded p-0.5 text-zinc-500 hover:bg-zinc-200"
                >
                  <ArrowLeft className="size-4" />
                </button>
                <span className="truncate text-sm font-medium text-zinc-700">
                  {selectedBo.key}
                </span>
                <span className="shrink-0 rounded-full bg-zinc-200 px-2 py-0.5 text-xs text-zinc-600">
                  {selectedBo.count.toLocaleString()}
                </span>
              </>
            ) : (
              <>
                <Database className="size-4 text-zinc-500" />
                <span className="text-sm font-medium text-zinc-700">Data Sets</span>
              </>
            )}
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto">
            {selectedBo ? (
              /* ─── Records Table ─── */
              <div className="flex h-full flex-col">
                {loading ? (
                  <div className="flex flex-1 items-center justify-center">
                    <Loader2 className="size-5 animate-spin text-zinc-400" />
                  </div>
                ) : records.length === 0 ? (
                  <div className="flex flex-1 items-center justify-center px-4 text-center text-xs text-zinc-400">
                    No records found
                  </div>
                ) : (
                  <>
                    <div className="flex-1 overflow-auto">
                      <table className="w-full text-left text-xs">
                        <thead className="sticky top-0 bg-zinc-100 text-zinc-500">
                          <tr>
                            {columns.map((col) => (
                              <th
                                key={col.key}
                                className="whitespace-nowrap px-3 py-2 font-medium"
                              >
                                {col.label}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-zinc-100">
                          {records.map((record, i) => (
                            <tr
                              key={record.id ?? i}
                              className="hover:bg-zinc-50"
                            >
                              {columns.map((col) => (
                                <td
                                  key={col.key}
                                  className="max-w-[140px] truncate whitespace-nowrap px-3 py-2 text-zinc-700"
                                  title={formatCellValue(record[col.key], col.key)}
                                >
                                  {formatCellValue(record[col.key], col.key)}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Pagination */}
                    {(hasPrev || hasMore) && (
                      <div className="flex items-center justify-between border-t border-zinc-200 px-3 py-2">
                        <span className="text-[10px] text-zinc-400">
                          {recordsOffset + 1}–{Math.min(recordsOffset + PAGE_SIZE, recordsTotal)} of{' '}
                          {recordsTotal.toLocaleString()}
                        </span>
                        <div className="flex gap-1">
                          <button
                            disabled={!hasPrev}
                            onClick={() => loadRecords(selectedBo, recordsOffset - PAGE_SIZE)}
                            className="rounded px-2 py-1 text-[10px] text-zinc-600 hover:bg-zinc-200 disabled:opacity-30"
                          >
                            Prev
                          </button>
                          <button
                            disabled={!hasMore}
                            onClick={() => loadRecords(selectedBo, recordsOffset + PAGE_SIZE)}
                            className="rounded px-2 py-1 text-[10px] text-zinc-600 hover:bg-zinc-200 disabled:opacity-30"
                          >
                            Next
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            ) : (
              /* ─── BO Card List ─── */
              <div className="p-2">
                {entries.length === 0 ? (
                  <div className="px-2 py-8 text-center text-xs text-zinc-400">
                    No data sets yet — ask the AI to search for something
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    {entries.map((bo) => {
                      const Icon = ENTITY_ICONS[bo.entityType] || Database;
                      return (
                        <button
                          key={bo.key}
                          onClick={() => openDetail(bo)}
                          className="w-full rounded-lg border border-zinc-200 bg-white p-3 text-left transition-shadow hover:shadow-sm"
                        >
                          <div className="flex items-start gap-2">
                            <Icon className="mt-0.5 size-4 shrink-0 text-zinc-400" />
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center justify-between gap-2">
                                <span className="truncate text-sm font-medium text-zinc-800">
                                  {bo.key}
                                </span>
                                <span className="shrink-0 rounded-full bg-zinc-100 px-2 py-0.5 text-xs font-medium text-zinc-600">
                                  {bo.count.toLocaleString()}
                                </span>
                              </div>
                              <p className="mt-0.5 line-clamp-2 text-xs text-zinc-500">
                                {bo.summary}
                              </p>
                              <div className="mt-1 flex items-center gap-2 text-[10px] text-zinc-400">
                                <span>{bo.entityType}</span>
                                <ChevronRight className="size-2.5" />
                                <span>{timeAgo(bo.updatedAt)}</span>
                              </div>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
